'use client';

import * as React from 'react';
import Link from 'next/link';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';

/**
 * PermissionDenied — canonical "you don't have access" state.
 *
 * Audit finding: route-level RBAC failures silently redirected to /admin with
 * no explanation, and widget-level gates simply hid elements. When access is
 * the reason a user can't see something they navigated to, say so and offer
 * a way out — permissions are exact-match per role, so "ask your admin" is
 * the correct next step, not a mystery redirect.
 *
 *   <PermissionDenied />                       // default copy + back home
 *   <PermissionDenied backHref="/admin" description="You need the reports:view permission." />
 */

export interface PermissionDeniedProps {
  title?: string;
  description?: string;
  /** Where the escape-hatch button points. */
  backHref?: string;
  backLabel?: string;
  /** Extra action (e.g. "Request access") rendered next to the back button. */
  action?: React.ReactNode;
  className?: string;
}

export function PermissionDenied({
  title = "You don't have access to this page",
  description = 'Your role does not include the required permission. Ask your workspace admin if you think this is a mistake.',
  backHref = '/admin',
  backLabel = 'Back to dashboard',
  action,
  className,
}: PermissionDeniedProps) {
  return (
    <EmptyState
      icon={ShieldAlert}
      title={title}
      description={description}
      className={className}
      action={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link href={backHref}>{backLabel}</Link>
          </Button>
          {action}
        </div>
      }
    />
  );
}
